import type { ReactNode } from "react";
import clsx from "clsx";
import ResponsiveCDNImage from "../../../components/common/image/responsive-cdn-image";
import {
  type AdminStoreListItem,
  AdminStoreRowSingle,
  AdminStoreRowMultiple,
  AdminStoreTableHeaderSingle,
  AdminStoreTableHeaderMultiple,
} from "./admin-store-select-row";

export type { AdminStoreListItem };

export interface AdminStoreTableWithMobileCardsProps {
  stores: AdminStoreListItem[];
  selectionMode: "single" | "multiple";
  selectedIds: number[];
  onSelect: (store: AdminStoreListItem) => void;
  /** 이미지 컬럼 표시 여부 (Single 모드만, 기본: true) */
  showImage?: boolean;
  /** 주소 컬럼/카드 표시 여부 (기본: true) */
  showAddress?: boolean;
  /** 테이블용 extra 헤더 (th 요소들) */
  extraHeaders?: ReactNode;
  /** 테이블용 extra 셀 (각 행), 없으면 extra 컬럼 없음 */
  renderExtraCells?: (store: AdminStoreListItem) => ReactNode;
  /** 모바일 카드에 표시할 추가 정보 (선택) */
  renderExtraCard?: (store: AdminStoreListItem) => ReactNode;
  /** Multiple 모드에서 비활성화할 store id 목록 */
  disabledStoreIds?: number[];
  emptyText?: string;
}

const DEFAULT_EMPTY_TEXT = "등록된 매장이 없습니다.";

export function AdminStoreTableWithMobileCards({
  stores,
  selectionMode,
  selectedIds,
  onSelect,
  showImage = true,
  showAddress = true,
  extraHeaders,
  renderExtraCells,
  renderExtraCard,
  disabledStoreIds = [],
  emptyText = DEFAULT_EMPTY_TEXT,
}: AdminStoreTableWithMobileCardsProps) {
  const isSelected = (id: number) => selectedIds.includes(id);
  const isDisabled = (id: number) => disabledStoreIds.includes(id);

  if (stores.length === 0) {
    return (
      <div className="px-4 py-8 text-center text-gray-500 text-sm">
        {emptyText}
      </div>
    );
  }

  return (
    <div className="break-keep">
      {/* 데스크톱: 테이블 + overflow-x-auto */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-gray-50 sticky top-0">
            {selectionMode === "single" ? (
              <AdminStoreTableHeaderSingle
                showImage={showImage}
                showAddress={showAddress}
                extraHeaders={extraHeaders}
              />
            ) : (
              <AdminStoreTableHeaderMultiple
                showAddress={showAddress}
                extraHeaders={extraHeaders}
              />
            )}
          </thead>
          <tbody className="divide-y divide-gray-200">
            {stores.map((store) =>
              selectionMode === "single" ? (
                <AdminStoreRowSingle
                  key={store.id}
                  store={store}
                  selected={isSelected(store.id)}
                  onSelect={() => onSelect(store)}
                  showImage={showImage}
                  showAddress={showAddress}
                  extraCells={renderExtraCells?.(store)}
                />
              ) : (
                <AdminStoreRowMultiple
                  key={store.id}
                  store={store}
                  checked={isSelected(store.id)}
                  onChange={() => onSelect(store)}
                  disabled={isDisabled(store.id)}
                  showAddress={showAddress}
                  extraCells={
                    <>
                      {renderExtraCells?.(store)}
                    </>
                  }
                />
              )
            )}
          </tbody>
        </table>
      </div>

      {/* 모바일: 카드 뷰 */}
      <div className="md:hidden space-y-4">
        {stores.map((store) => {
          const selected = isSelected(store.id);
          const disabled = isDisabled(store.id);
          return (
            <div
              key={store.id}
              className={clsx(
                "bg-white rounded-lg border border-gray-200 p-4",
                !disabled && "cursor-pointer active:opacity-80",
                disabled && "opacity-60"
              )}
              role={!disabled ? "button" : undefined}
              tabIndex={!disabled ? 0 : undefined}
              onClick={() => !disabled && onSelect(store)}
              onKeyDown={
                !disabled
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onSelect(store);
                      }
                    }
                  : undefined
              }
            >
              <div className="flex items-start gap-4">
                {(selectionMode === "multiple" || showImage) ? (
                  store.img ? (
                    <ResponsiveCDNImage
                      src={store.img}
                      alt={store.title}
                      className="w-16 h-16 rounded-md flex-shrink-0 object-cover"
                      sizeRatio={0.25}
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-md bg-gray-200 flex-shrink-0" />
                  )
                ) : null}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-gray-500">ID {store.id}</span>
                    <h3 className="font-medium text-gray-900 truncate">
                      {store.title}
                    </h3>
                  </div>
                  {showAddress && (
                    <p className="text-sm text-gray-500 mt-1 break-keep">
                      {store.address ?? "주소 없음"}
                    </p>
                  )}
                  {renderExtraCard?.(store)}
                </div>
                <div className="flex-shrink-0">
                  {selectionMode === "single" ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!disabled) onSelect(store);
                      }}
                      disabled={disabled}
                      className={clsx(
                        "px-4 py-2 rounded-md text-sm font-medium",
                        selected
                          ? "bg-blue-50 text-blue-700 border border-blue-200"
                          : "bg-blue-500 text-white hover:bg-blue-600",
                        disabled && "opacity-50 cursor-not-allowed"
                      )}
                    >
                      {selected ? "선택됨" : "선택"}
                    </button>
                  ) : (
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => !disabled && onSelect(store)}
                      disabled={disabled}
                      className="w-6 h-6 rounded border-gray-300"
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
